import React, { useState } from "react";
import PropTypes from "prop-types";
import { 
  TextField, 
  Grid, 
  Button, 
  Box,
  Typography,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import {
  Lock,
  LockOpen,
  VpnKey,
  Save
} from '@mui/icons-material';

const textFieldStyles = {
  "& label.Mui-focused": {
    color: "#536777",
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset, &:hover fieldset": {
      borderColor: "#05CC98",
      opacity: 0.24,
    },
  },
};

const passwordFields = [
  {
    id: "current_password",
    name: "current_password",
    label: "Senha atual",
    icon: <VpnKey sx={{ color: 'action.active' }} />,
    tooltip: "Digite sua senha atual"
  },
  {
    id: "password",
    name: "password",
    label: "Nova senha",
    icon: <LockOpen sx={{ color: 'action.active' }} />,
    tooltip: "Digite sua nova senha"
  },
  {
    id: "password_confirmation",
    name: "password_confirmation",
    label: "Confirmação",
    icon: <Lock sx={{ color: 'action.active' }} />,
    tooltip: "Confirme sua nova senha"
  }
];

export default function ChangePasswordForm({ currentUser, updateUser }) {
  const [loading, setLoading] = useState(false);
  const [userErrors, setUserErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const [user, setUser] = useState({
    id: currentUser.id,
    password: "",
    password_confirmation: "",
    current_password: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
  };

  const areFieldsEmpty = () => {
    return !user.password || !user.password_confirmation || !user.current_password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await updateUser(user);
      showMessage(res.data.message, 'success');
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      showMessage(err.response?.data.message, 'error');
      setUserErrors(err.response?.data.errors || {});
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showMessage = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const renderPasswordField = ({ id, name, label, icon, tooltip }) => (
    <Box 
      sx={{ 
        display: "flex",
        alignItems: "flex-start",
        mb: 2,
        gap: 2
      }}
    >
      <Tooltip title={tooltip} placement="left" arrow>
        <Box 
          sx={{ 
            mt: 1.5,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {icon}
        </Box>
      </Tooltip>

      <TextField
        fullWidth
        type="password"
        id={id}
        name={name}
        label={label}
        variant="outlined"
        required
        value={user[name]}
        error={Boolean(userErrors[name])}
        helperText={userErrors[name]?.join(", ") || ""}
        onChange={handleChange}
        sx={textFieldStyles}
      />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h6" 
          color="textSecondary"
          sx={{
            fontWeight: 500
          }}
        >
          Alterar Senha
        </Typography>
      </Box>

      <Grid container>
        <form onSubmit={handleSubmit} noValidate style={{ width: "100%" }}>
          <Grid 
            item 
            xs={12} 
            sx={{ 
              display: "flex",
              flexDirection: "column"
            }}
          >
            {passwordFields.map(renderPasswordField)}

            <Box 
              sx={{ 
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mt: 3
              }}
            >
              <Button
                variant="contained"
                size="large"
                type="submit"
                disabled={loading || areFieldsEmpty()}
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                disableElevation
                sx={{
                  color: "#fff",
                  minWidth: "120px",
                  borderRadius: "10px",
                  fontWeight: 900,
                  bgcolor: "#239CC4",
                  "&:hover": {
                    bgcolor: "#1b7a99"
                  },
                  "&:disabled": {
                    bgcolor: "action.disabledBackground"
                  }
                }}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Grid>
        </form>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

ChangePasswordForm.propTypes = {
  currentUser: PropTypes.object.isRequired,
  updateUser: PropTypes.func.isRequired
};
